import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, Stack, Tooltip, Typography } from '@mui/material'
import type { DateTime } from 'luxon'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import type { FormattedRow } from '@/features/bessDashboard/types'
import LevelPricingCard from '@/features/bidding/components/cascading/configuration/LevelPricingCard'
import StackedForecastCharts from '@/features/bidding/components/cascading/configuration/StackedForecastCharts'
import { useForecastMarketAvailability } from '@/features/bidding/hooks/useForecastMarketAvailability'
import type { UsePricingStrategyStateReturn } from '@/features/bidding/hooks/usePricingStrategyState'
import type { ForecastMarketPair, PriceQuantile, PricingMode } from '@/features/bidding/types/forecastPricing'
import { MARKET_PAIR_OPTIONS } from '@/features/bidding/types/forecastPricing'

type ConfigurationTabContentProps = {
  pricingStrategy: UsePricingStrategyStateReturn
  forecastsData: FormattedRow[] | undefined
  selectedDeliveryDay: DateTime
}

function ConfigurationTabContent({
  pricingStrategy,
  forecastsData,
  selectedDeliveryDay,
}: Readonly<ConfigurationTabContentProps>) {
  const { t } = useTranslation()
  const { config, focusedLevelIndex, setFocusedLevelIndex } = pricingStrategy

  const { marketAvailability, unavailableMarkets } = useForecastMarketAvailability({
    forecastsData,
    selectedDate: selectedDeliveryDay,
  })

  // Forecast is available if at least one market has data
  const isForecastAvailable = unavailableMarkets.size < MARKET_PAIR_OPTIONS.length

  const handleModeChange = useCallback(
    (levelId: string, currentMarket: ForecastMarketPair) => (mode: PricingMode) => {
      pricingStrategy.updateMode(levelId, mode)

      // When switching to forecast mode, ensure the selected market has data
      if (mode === 'forecast') {
        const currentAvailability = marketAvailability.get(currentMarket)
        if (!currentAvailability?.hasData) {
          // Find first market with available data
          const availableMarket = MARKET_PAIR_OPTIONS.find((option) => {
            const availability = marketAvailability.get(option.value)
            return availability?.hasData
          })
          if (availableMarket) {
            pricingStrategy.updateMarket(levelId, availableMarket.value)
          }
        }
      }
    },
    [pricingStrategy, marketAvailability],
  )

  const handleMarketChange = useCallback(
    (levelId: string) => (market: ForecastMarketPair) => {
      pricingStrategy.updateMarket(levelId, market)
    },
    [pricingStrategy],
  )

  const handleQuantileChange = useCallback(
    (levelId: string) => (quantile: PriceQuantile) => {
      pricingStrategy.updateQuantile(levelId, quantile)
    },
    [pricingStrategy],
  )

  const focusedConfig = focusedLevelIndex !== null ? config.levels[focusedLevelIndex] : null

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      {/* Section Header */}
      <Box
        sx={{
          px: 2,
          pt: 2,
          pb: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            sx={{
              fontSize: '1rem',
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            {t('bidding.bidding_cascading.configuration.price_strategy_title')}
          </Typography>
          <Tooltip title={t('bidding.bidding_cascading.configuration.price_strategy_description')}>
            <InfoOutlinedIcon
              sx={{
                fontSize: '1rem',
                color: 'text.secondary',
                cursor: 'help',
                '&:hover': { color: 'primary.main' },
              }}
            />
          </Tooltip>
        </Box>
      </Box>

      {/* Scrollable Content */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          minHeight: 0,
        }}
      >
        {/* Level Configuration Cards - reversed to match bid ladder display order */}
        <Stack spacing={1.5} sx={{ p: 2 }}>
          {[...config.levels].reverse().map((levelConfig) => {
            const originalIndex = config.levels.findIndex((l) => l.levelId === levelConfig.levelId)
            return (
              <LevelPricingCard
                key={levelConfig.levelId}
                config={levelConfig}
                isFocused={focusedLevelIndex === originalIndex}
                isForecastAvailable={isForecastAvailable}
                levelIndex={originalIndex}
                marketAvailability={marketAvailability}
                minQuantile={pricingStrategy.getMinQuantileForLevel(originalIndex)}
                onFocus={() => setFocusedLevelIndex(originalIndex)}
                onMarketChange={handleMarketChange(levelConfig.levelId)}
                onModeChange={handleModeChange(levelConfig.levelId, levelConfig.market)}
                onQuantileChange={handleQuantileChange(levelConfig.levelId)}
              />
            )
          })}
        </Stack>

        {/* Preview Section */}
        {focusedConfig && focusedConfig.mode === 'forecast' && (
          <Box
            sx={{
              px: 2,
              pb: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: 'text.primary',
                py: 1,
              }}
            >
              {t('bidding.bidding_cascading.configuration.preview_title', {
                level: (focusedLevelIndex ?? 0) + 1,
              })}
            </Typography>
            <StackedForecastCharts
              forecastsData={forecastsData}
              market={focusedConfig.market}
              quantile={focusedConfig.quantile}
              selectedDeliveryDay={selectedDeliveryDay}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default ConfigurationTabContent
